import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import useEscapeToClose from '../hooks/useEscapeToClose';
import colors from '../theme/colors';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: ${colors.base.themePrimaryTransparent};
  border-radius: 16px;
  padding: 40px;
  padding-top: 50px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  min-height: 500px;
  overflow-y: auto;
  position: relative;
  box-shadow: ${colors.effects.shadowLarge};
  border: 1px solid ${colors.border.light};

  @media (max-width: 768px) {
    padding: 30px 20px;
    min-height: 400px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${colors.background.white10};
  border: 1px solid ${colors.border.light};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.white20};
    transform: rotate(90deg);
  }
`;

const ModalTitle = styled.h2`
  color: ${colors.accent.secondary};
  margin: 0 0 20px 0;
  font-size: 1.6rem;
  font-weight: 700;
  text-shadow: ${colors.effects.textShadowGlow};
  text-align: left;
`;

const BackButton = styled(motion.button)`
  background: ${colors.button.aboutBg};
  border: 1px solid ${colors.button.aboutBorder};
  border-radius: 20px;
  color: ${colors.button.aboutText};
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  box-shadow: ${colors.effects.shadow};
  margin-bottom: 20px;

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-1px);
    box-shadow: ${colors.effects.shadowHover};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 25px;
  background: white;
  border-radius: 12px;
  box-shadow: ${colors.effects.shadow};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: ${colors.primary.dark90};
  font-size: 14px;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${colors.border.gray};
  background: ${colors.background.lightGray};
  color: ${colors.text.dark};
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.accent.orange};
    background: white;
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${colors.border.gray};
  background: ${colors.background.lightGray};
  color: ${colors.text.dark};
  font-size: 14px;
  min-height: 30vh;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.accent.orange};
    background: white;
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  border: 1px solid ${colors.button.primaryBorder};
  background: ${colors.button.primaryBg};
  color: ${colors.button.primaryText};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.button.primaryBgHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${colors.primary.darkRgbaLight};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;

  &.success {
    background: ${colors.status.successBg};
    border: 1px solid ${colors.status.successBorder};
    color: ${colors.status.successText};
  }

  &.error {
    background: ${colors.status.errorBg};
    border: 1px solid ${colors.status.errorBorder};
    color: ${colors.status.errorText};
  }
`;

const ContactFormModal = ({ isOpen, onClose, onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEscapeToClose(isOpen, onClose);

  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    setEmailStatus(null);

    try {
      // Use user's email if logged in, otherwise use form email
      const emailToSend = user ? user.email : contactForm.email;

      const response = await fetch('/api/contact/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactForm.name,
          email: emailToSend,
          message: contactForm.message
        })
      });

      const data = await response.json();

      if (data.success) {
        setEmailStatus('success');
        setContactForm({ name: '', email: '', message: '' });
        setTimeout(() => {
          onClose();
          setEmailStatus(null);
        }, 3000);
      } else {
        setEmailStatus('error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus('error');
    } finally {
      setSendingEmail(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>×</CloseButton>

            <ModalTitle>📧 {t('common.contactMe') || 'Contact Me'}</ModalTitle>

            {onBack && (
              <BackButton onClick={onBack}>
                ← {t('common.back') || 'Back'}
              </BackButton>
            )}

            <ContactForm onSubmit={handleContactSubmit}>
              <FormGroup>
                <Label htmlFor="contact-name">
                  {t('common.yourName') || 'Your Name'} *
                </Label>
                <Input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={handleContactFormChange}
                  placeholder={t('common.namePlaceholder') || 'John Doe'}
                />
              </FormGroup>

              {/* Only show email field if user is not logged in */}
              {!user && (
                <FormGroup>
                  <Label htmlFor="contact-email">
                    {t('common.yourEmail') || 'Your Email'} *
                  </Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={handleContactFormChange}
                    placeholder={t('common.emailPlaceholder') || 'john@example.com'}
                  />
                </FormGroup>
              )}

              {/* Show logged-in user's email */}
              {user && (
                <FormGroup>
                  <Label>
                    {t('common.yourEmail') || 'Your Email'}
                  </Label>
                  <Input
                    type="email"
                    value={user.email}
                    disabled
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="contact-message">
                  {t('common.yourMessage') || 'Your Message'} *
                </Label>
                <TextArea
                  id="contact-message"
                  name="message"
                  required
                  value={contactForm.message}
                  onChange={handleContactFormChange}
                  placeholder={t('common.messagePlaceholder') || 'Your message here...'}
                />
              </FormGroup>

              {emailStatus && (
                <StatusMessage className={emailStatus}>
                  {emailStatus === 'success'
                    ? (t('common.messageSent') || '✓ Message sent successfully!')
                    : (t('common.messageError') || '✗ Error sending message. Please try again.')
                  }
                </StatusMessage>
              )}

              <SubmitButton type="submit" disabled={sendingEmail}>
                {sendingEmail
                  ? (t('common.sending') || 'Sending...')
                  : (t('common.sendMessage') || 'Send Message')
                }
              </SubmitButton>
            </ContactForm>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ContactFormModal;
